import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BrainCircuit, Upload } from "lucide-react";

const ALGORITHMS = [
  { id: "auto", label: "Auto — follows the project brief" },
  { id: "random_forest", label: "Random Forest" },
  { id: "gradient_boost", label: "Gradient Boosting" },
  { id: "logistic", label: "Logistic / Linear" },
  { id: "decision_tree", label: "Decision Tree" },
  { id: "svm", label: "Support Vector Machine (SVM)" },
  { id: "knn", label: "K-Nearest Neighbors (KNN)" },
  { id: "naive_bayes", label: "Naive Bayes" },
  { id: "neural_net", label: "Neural Network (Deep Learning)" },
];

// Per-project real ML: upload the client's CSV, pick the answer column(s) and the
// input columns, train on the server, then the published site/APK gives REAL
// predictions (no more fake JS).
export default function MLModelPanel({ projectId, onTrained }) {
  const [dataset, setDataset] = useState(null);
  const [model, setModel] = useState(null);
  const [models, setModels] = useState({});
  const [targets, setTargets] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [algorithm, setAlgorithm] = useState("auto");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/dataset`);
      setDataset(data.dataset);
      setModel(data.model);
      setModels(data.models || {});
      if (data.dataset && targets.length === 0) {
        const cols = data.dataset.columns.map((c) => c.name);
        setTargets((t) => (t.length ? t : [cols[cols.length - 1]]));
      }
    } catch { /* no dataset yet */ }
  };

  useEffect(() => { load(); }, [projectId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/projects/${projectId}/dataset`, form);
      setDataset(data.dataset);
      setModel(null);
      setModels({});
      setTargets([data.dataset.columns[data.dataset.columns.length - 1].name]);
      setExcluded([]);
      toast.success(`Dataset loaded — ${data.dataset.rows} rows, ${data.dataset.columns.length} columns`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggle = (list, setList, name) =>
    setList(list.includes(name) ? list.filter((c) => c !== name) : [...list, name]);

  const train = async () => {
    if (!targets.length) return;
    setBusy(true);
    try {
      const featureCols = dataset.columns
        .map((c) => c.name)
        .filter((c) => !targets.includes(c) && !excluded.includes(c));
      const { data } = await api.post(`/projects/${projectId}/train`, {
        target: targets[0],
        targets,
        algorithm,
        features: featureCols,
      });
      setModel(data.model);
      setModels(data.models || {});
      const parts = Object.entries(data.models || {}).map(
        ([t, m]) => `${t}: ${Math.round(m.score * 100)}%`
      );
      toast.success(`Trained ${Object.keys(data.models || {}).length} model(s) — ${parts.join(" · ")}`);
      if (Object.keys(data.failures || {}).length) {
        toast.warning(`Could not train: ${Object.keys(data.failures).join(", ")}`);
      }
      onTrained?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white text-xs focus:outline-none focus:border-cyan-600 transition-all";
  const chipCls = (on) =>
    `px-2 py-1 rounded-md border text-[11px] cursor-pointer transition-colors ${
      on
        ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-500/10 text-cyan-800 dark:text-cyan-200"
        : "border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-cyan-400"
    }`;

  return (
    <div className="accent-bar premium-card p-4 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08]" data-testid="ml-panel">
      <div className="flex items-center gap-2 mb-1">
        <BrainCircuit className="w-4 h-4 text-cyan-600 dark:text-cyan-300" />
        <div className="text-xs uppercase code-font neon-text font-bold tracking-wider">Real ML Model</div>
      </div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
        Upload the client's dataset and train a real model — the published site and APK then give REAL predictions from that data, not simulated answers.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={upload} data-testid="ml-dataset-input" />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-[11px] font-bold uppercase tracking-wider hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-colors disabled:opacity-60"
          data-testid="ml-upload-button"
        >
          <Upload className="w-3.5 h-3.5" /> {dataset ? "Replace dataset" : "Upload dataset (CSV)"}
        </button>
        {dataset && (
          <span className="text-[11px] code-font text-slate-500 dark:text-slate-400" data-testid="ml-dataset-info">
            {dataset.filename} · {dataset.rows} rows
          </span>
        )}
      </div>

      {dataset && (
        <>
          <div className="mt-3" data-testid="ml-target-picker">
            <div className="text-[10px] uppercase code-font font-bold tracking-wider text-slate-500 dark:text-slate-400 mb-1">
              Answer column(s) to predict — pick one or many
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
              {dataset.columns.map((c) => (
                <button key={c.name} type="button" className={chipCls(targets.includes(c.name))}
                  onClick={() => toggle(targets, setTargets, c.name)}
                  data-testid={`ml-target-${c.name}`}>
                  {c.name} ({c.kind})
                </button>
              ))}
            </div>
          </div>

          <div className="mt-2" data-testid="ml-feature-picker">
            <div className="text-[10px] uppercase code-font font-bold tracking-wider text-slate-500 dark:text-slate-400 mb-1">
              Input columns — tap to exclude any you don't want
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
              {dataset.columns.filter((c) => !targets.includes(c.name)).map((c) => (
                <button key={c.name} type="button" className={chipCls(!excluded.includes(c.name))}
                  onClick={() => toggle(excluded, setExcluded, c.name)}
                  data-testid={`ml-feature-${c.name}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select className={inputCls} value={algorithm} onChange={(e) => setAlgorithm(e.target.value)} data-testid="ml-algorithm-select">
              {ALGORITHMS.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
            <button
              onClick={train}
              disabled={busy || !targets.length}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-sky-600 dark:from-cyan-400 dark:to-sky-400 text-white dark:text-[#090D16] text-[11px] font-bold uppercase tracking-wider hover:from-cyan-700 hover:to-sky-700 transition-all disabled:opacity-60"
              data-testid="ml-train-button"
            >
              {busy ? "Working…" : model ? `Retrain (${targets.length})` : targets.length > 1 ? `Train ${targets.length} Models` : "Train Model"}
            </button>
          </div>
        </>
      )}

      {Object.keys(models).length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="ml-model-status">
          {Object.entries(models).map(([t, m]) => (
            <div key={t} className="rounded-lg border border-emerald-300/60 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-800 dark:text-emerald-200">
              <span className="font-bold">Trained:</span> {m.algorithm_label} · {m.kind} ·{" "}
              <span className="font-bold">{Math.round(m.score * 100)}% {m.metric_name === "r2" ? "R²" : "accuracy"}</span>{" "}
              on held-out test data · predicts <span className="code-font">{t}</span>
            </div>
          ))}
          <p className="text-[10px] text-slate-500 dark:text-slate-400">The live site/APK uses these models.</p>
        </div>
      )}
    </div>
  );
}
